'use client';

import { FC } from 'react';
import { asLink, isFilled } from '@prismicio/client';
import type { KeyTextField, LinkField, RichTextField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { FormLayout, FormRedirect } from '@/components';
import { GoogleReCaptchaContext } from '@/context';
import { HubspotFormData } from '@/interfaces';

const HUBSPOT_PORTAL_ID = '7119515';

interface FormCardProps {
  /**
   * HubSpot field schema fetched server-side in the slice dispatcher. `null`
   * when no `form_id` is configured — the card shows a clear "not configured"
   * placeholder in that case instead of HubSpot's generic fallback form.
   */
  formData: HubspotFormData | null;
  formTitle?: KeyTextField;
  formId?: KeyTextField;
  footnote?: RichTextField;
  /** Prismic Select, raw string value (e.g. "Plain" / "Bordered"). */
  formFieldStyle?: string | null;
  /** Prismic Select, raw string value (e.g. "Default" / "Column" / "Full width"). */
  formFieldLayout?: string | null;
  successContent?: RichTextField;
  redirectLink?: LinkField;
  /**
   * Positional / layout classes for the outer card (e.g. desktop grid
   * placement). Chrome styling (padding, radius, bg) is owned by this
   * component and should not be overridden through here.
   */
  className?: string;
}

/**
 * Shared form card used by both `imageLeftFormRight` and `contentFormSplit`
 * variants of `LeadCaptureHero`.
 *
 * Encapsulates the identical card chrome (light grey panel, rounded 10px, 20px
 * padding), the "form id not configured" fallback, the reCAPTCHA v3 gate, and
 * the full `FormLayout` wiring (field style/layout, submit button, terms,
 * success state, footnote). Only the outer positioning (which column / row
 * the card sits in on desktop) is passed in via `className`, so variants keep
 * independent control of their grid layout while sharing the submission
 * workflow and styling spec.
 */
const FormCard: FC<FormCardProps> = ({
  formData,
  formTitle,
  formId,
  footnote,
  formFieldStyle,
  formFieldLayout,
  successContent,
  redirectLink,
  className = '',
}) => {
  // Normalize the Select values so casing / underscores don't break matching.
  const layout = (formFieldLayout ?? '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .trim();
  const bordered = (formFieldStyle ?? '').toLowerCase().trim() === 'bordered';

  const inputBase =
    'h-12 w-full rounded-lg bg-white px-4 font-inter text-[18px] font-light leading-[1.5] text-[#1c2228] placeholder-gray-400 outline-none focus:ring-1 focus:ring-[#00F557]';

  // Field arrangement is the only thing the layout option changes; our field/
  // label/checkbox/submit styling is applied in every case. "Full width" puts
  // one field per row; every other option (Default, Column) uses the 2-col grid.
  const fieldRowClassName =
    layout === 'full width'
      ? 'grid grid-cols-1 gap-4'
      : 'grid grid-cols-1 gap-4 sm:grid-cols-2';

  const inputClassName = `${inputBase} ${bordered ? 'border border-gray-300' : 'border-0'}`;
  const checkboxClassName = `mt-1 shrink-0 cursor-pointer ${bordered ? '' : '!border-0'}`;

  // Optional post-submit redirect target (internal doc or external URL).
  const redirectUrl = isFilled.link(redirectLink)
    ? (asLink(redirectLink) ?? undefined)
    : undefined;
  const redirectMode: FormRedirect | undefined = redirectUrl
    ? { mode: 'link', url: redirectUrl }
    : undefined;

  return (
    <div
      className={`flex w-full flex-col gap-5 rounded-[10px] bg-[#F3F3F3] p-5 ${className}`}
    >
      {isFilled.keyText(formTitle) && (
        <p className="font-inter text-[18px] font-semibold leading-[1.2] text-[#1c2228]">
          {formTitle}
        </p>
      )}

      {!formData || !formId ? (
        <p className="rounded-lg bg-white px-3 py-4 text-center text-[14px] text-[#5b6166]">
          This form isn&apos;t configured yet. Add a HubSpot{' '}
          <span className="font-semibold">form id</span> to this slice in
          Prismic.
        </p>
      ) : (
        <GoogleReCaptchaContext>
          <FormLayout
            formData={formData}
            hubspotPortalIdForm={HUBSPOT_PORTAL_ID}
            hubspotGuidForm={formId}
            enforceCorporateEmail={false}
            renderTerms={false}
            redirect={redirectMode}
            showFieldLabels
            submitLabel="Submit entry"
            formContainerClass="flex flex-col gap-4"
            fieldRowClassName={fieldRowClassName}
            labelClassName="mb-2 block font-inter text-[16px] font-semibold leading-[1.5] text-black"
            inputClassName={inputClassName}
            submitClassName="mt-2 inline-flex items-center justify-center gap-2 self-start rounded-lg bg-[#00F557] px-8 py-3 text-[16px] font-bold text-[#002726] transition-colors hover:bg-[#00D964] disabled:opacity-50 [&_.arrow-icon]:hidden"
            termsClassName="cursor-pointer font-inter text-[12px] font-light leading-[1.4] text-black [&_a]:text-[#017a66] [&_a]:underline"
            checkboxClassName={checkboxClassName}
            successContent={
              isFilled.richText(successContent) ? successContent : undefined
            }
            successClassName="flex flex-col items-center justify-center gap-y-6 py-5 text-center"
            footer={
              isFilled.richText(footnote) ? (
                <div className="font-inter text-[12px] font-light leading-[1.4] text-black">
                  <PrismicRichText
                    field={footnote}
                    components={{
                      paragraph: ({ children }) => (
                        <p className="mb-1 last:mb-0">{children}</p>
                      ),
                    }}
                  />
                </div>
              ) : null
            }
          />
        </GoogleReCaptchaContext>
      )}
    </div>
  );
};

export default FormCard;
